import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Stack,
  Textarea,
  CircularProgress,
  Alert,
  Grid,
  AspectRatio
} from '@mui/joy';
import {
  Check as CheckIcon,
  Image as ImageIcon,
  Sparkle as GenerateIcon
} from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { Review } from '../../types/marketing';

interface Template {
  id: number;
  name: string;
  description: string;
}

interface TestimonialTemplateSelectorProps {
  open: boolean;
  onClose: () => void;
  review: Review;
  onGenerate: (media: any) => void;
}

// Template preview colors/styles for visual representation
const templateStyles: Record<number, { bg: string; accent: string; textColor: string }> = {
  1: { bg: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 100%)', accent: '#00bcd4', textColor: '#fff' },
  2: { bg: '#000', accent: '#1e3a5f', textColor: '#fff' },
  3: { bg: 'linear-gradient(135deg, #0a1628 50%, #fff 50%)', accent: '#0a1628', textColor: '#fff' },
  4: { bg: '#0a1628', accent: '#00bcd4', textColor: '#fff' },
  5: { bg: 'linear-gradient(180deg, #0a1628 0%, #1e3a5f 100%)', accent: '#fff', textColor: '#0a1628' },
  6: { bg: '#141418', accent: '#0a1628', textColor: '#fff' }
};

export const TestimonialTemplateSelector: React.FC<TestimonialTemplateSelectorProps> = ({
  open,
  onClose,
  review,
  onGenerate
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<number>(1);
  const [customText, setCustomText] = useState<string>(review.review_text);

  // Fetch available templates
  const { data: templatesData } = useQuery({
    queryKey: ['testimonial-templates'],
    queryFn: async () => {
      const response = await api.get('/api/marketing/social-posts/testimonial-templates/');
      return response.data as { templates: Template[] };
    }
  });

  const templates: Template[] = templatesData?.templates || [
    { id: 1, name: 'Dark Gradient with Header', description: 'Dark gradient with glowing accents' },
    { id: 2, name: 'Dark Minimal', description: 'Clean minimal design' },
    { id: 3, name: 'Split Diagonal', description: 'Bold diagonal split' },
    { id: 4, name: 'Large Quote Marks', description: 'Oversized quotation marks' },
    { id: 5, name: 'White Card', description: 'White card on gradient' },
    { id: 6, name: 'Accent Bar', description: 'Vertical accent stripe' }
  ];

  // Generate image mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/api/marketing/social-posts/generate-testimonial-image/', {
        review_id: review.id,
        template_id: selectedTemplate,
        custom_text: customText !== review.review_text ? customText : undefined
      });
      return response.data as { media: any };
    },
    onSuccess: (data) => {
      onGenerate(data.media);
      onClose();
    }
  });

  const handleGenerate = () => {
    generateMutation.mutate();
  };

  // Reset custom text when review changes
  React.useEffect(() => {
    setCustomText(review.review_text);
  }, [review.review_text]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog size="lg" sx={{ width: '90%', maxWidth: 1000, maxHeight: '90vh', overflow: 'hidden' }}>
        <ModalClose />
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon />
            Generate Testimonial Image
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto' }}>
          <Stack spacing={3}>
            {/* Review Info */}
            <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: 'md' }}>
              <Typography level="body-sm" sx={{ mb: 0.5, color: 'text.tertiary' }}>
                Creating image for review by:
              </Typography>
              <Typography level="title-md">
                {review.reviewer_name} - {review.rating} Stars
              </Typography>
            </Box>

            {/* Template Selection */}
            <Box>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Choose a Template
              </Typography>
              <Grid container spacing={2}>
                {templates.map((template) => {
                  const style = templateStyles[template.id] || templateStyles[1];
                  const isSelected = selectedTemplate === template.id;

                  return (
                    <Grid key={template.id} xs={6} sm={4}>
                      <Card
                        variant={isSelected ? 'solid' : 'outlined'}
                        color={isSelected ? 'primary' : 'neutral'}
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: isSelected ? '2px solid' : '1px solid',
                          borderColor: isSelected ? 'primary.500' : 'neutral.outlinedBorder',
                          '&:hover': {
                            boxShadow: 'md',
                            borderColor: 'primary.300'
                          }
                        }}
                        onClick={() => setSelectedTemplate(template.id)}
                      >
                        {/* Template Preview */}
                        <AspectRatio ratio="1" sx={{ mb: 1 }}>
                          <Box
                            sx={{
                              background: style.bg,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 2,
                              position: 'relative'
                            }}
                          >
                            {/* Template number badge */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: style.accent,
                                color: style.textColor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 12,
                                fontWeight: 'bold'
                              }}
                            >
                              {template.id}
                            </Box>

                            {/* Simple visual representation */}
                            <Box
                              sx={{
                                width: '80%',
                                p: 1,
                                border: `1px solid ${style.accent}`,
                                borderRadius: 'sm',
                                bgcolor: template.id === 5 ? '#fff' : 'transparent'
                              }}
                            >
                              <Typography
                                level="body-xs"
                                sx={{
                                  color: template.id === 5 ? style.textColor : style.textColor,
                                  textAlign: 'center',
                                  fontSize: 8
                                }}
                              >
                                "Quote text here..."
                              </Typography>
                            </Box>

                            {/* Selection indicator */}
                            {isSelected && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  left: 8,
                                  bgcolor: 'var(--brand-text, #7013f5)',
                                  borderRadius: '50%',
                                  p: 0.5,
                                  display: 'flex'
                                }}
                              >
                                <CheckIcon size={16} color="#fff" />
                              </Box>
                            )}
                          </Box>
                        </AspectRatio>

                        {/* Template name */}
                        <Typography
                          level="body-sm"
                          fontWeight="lg"
                          sx={{
                            textAlign: 'center',
                            color: isSelected ? 'primary.plainColor' : 'text.primary'
                          }}
                        >
                          {template.name}
                        </Typography>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>

            {/* Editable Quote Text */}
            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Quote Text
              </Typography>
              <Typography level="body-xs" sx={{ mb: 1, color: 'text.tertiary' }}>
                Edit the text below if you want to shorten or modify the quote
              </Typography>
              <Textarea
                value={customText}
                onChange={(e) => setCustomText(e.target.value)}
                minRows={3}
                maxRows={6}
                placeholder="Enter quote text..."
                sx={{ width: '100%' }}
              />
              <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary' }}>
                {customText.length} characters
                {customText.length > 300 && (
                  <Typography component="span" color="warning" sx={{ ml: 1 }}>
                    (Consider shortening for better readability)
                  </Typography>
                )}
              </Typography>
            </Box>

            {/* Error Message */}
            {generateMutation.isError && (
              <Alert color="danger">
                {(generateMutation.error as any)?.response?.data?.error || 'Failed to generate image. Please try again.'}
              </Alert>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2 }}>
              <Button variant="plain" onClick={onClose} disabled={generateMutation.isPending}>
                Cancel
              </Button>
              <Button
                startDecorator={
                  generateMutation.isPending ? (
                    <CircularProgress size="sm" />
                  ) : (
                    <GenerateIcon />
                  )
                }
                onClick={handleGenerate}
                disabled={generateMutation.isPending || !customText.trim()}
                loading={generateMutation.isPending}
              >
                {generateMutation.isPending ? 'Generating...' : 'Generate Image'}
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};
